import React, { useContext, useEffect } from "react";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import { useQueryProduct } from "../../../queries/product/hooks/useQueryProduct";
import { CircularProgress, Tooltip } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MedicationIcon from "@mui/icons-material/Medication";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import { formatPrice, getSealDetailsDisplay } from "../../utils/utils";
import moment from "moment/moment";
import { UserContext } from "../../../hooks/userContext";
import Box from "@mui/material/Box";
import axios from "axios";
import BuyAction from "../../../routes/Buy/BuyAction";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

const Tag = ({ label, tone }: { label: string; tone: "blue" | "neutral" | "green" | "amber" }) => {
  const tones = {
    blue: { color: PRIMARY_DARK, bg: "#F1F5F9", dot: PRIMARY },
    neutral: { color: INK_SOFT, bg: LINE_SOFT, dot: "#475569" },
    green: { color: "#2E7D32", bg: "#E8F5E9", dot: "#66BB6A" },
    amber: { color: "#B45309", bg: "#FEF3C7", dot: "#F59E0B" },
  } as const;
  const t = tones[tone];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        px: "8px",
        py: "3px",
        borderRadius: "5px",
        fontFamily: MONO_FONT,
        fontSize: "10.5px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        color: t.color,
        backgroundColor: t.bg,
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: t.dot, flexShrink: 0 }} />
      {label}
    </Box>
  );
};

const InfoBox = ({ title, children, sx }: { title: string; children: React.ReactNode; sx?: any }) => (
  <Box
    sx={{
      bgcolor: "#FFFFFF",
      border: `1px solid ${LINE}`,
      borderRadius: "10px",
      p: 2,
      height: "100%",
      fontFamily: BODY_FONT,
      ...sx,
    }}
  >
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: INK_SOFT,
        mb: 1,
      }}
    >
      {title}
    </Box>
    {children}
  </Box>
);

const DetailRow = ({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 2,
      py: 0.9,
      borderBottom: `1px solid ${LINE_SOFT}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Box sx={{ fontSize: "12.5px", color: INK_SOFT, flexShrink: 0 }}>{label}</Box>
    <Box
      sx={{
        fontSize: mono ? "12.5px" : "13.5px",
        fontWeight: 600,
        color: INK,
        textAlign: "right",
        fontFamily: mono ? MONO_FONT : BODY_FONT,
        minWidth: 0,
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Box>
  </Box>
);

interface ActionRequiredOrdersProps {
  params: any;
  setParams: (params: any) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  handleCart: (params: any, packageAvailable?: number) => void;
  loading: boolean;
}

export const ProductDialog: React.FunctionComponent<
  ActionRequiredOrdersProps
> = ({
  params,
  setParams,
  open,
  setOpen,
  handleCart,
  loading: cartLoading,
}) => {
  const { id }: any = useContext(UserContext);
  const { loading, data } = useQueryProduct({
    variables: { where: { id: params.id } },
    fetchPolicy: "no-cache",
  });

  const [imageUrl, setImageUrl] = React.useState<string | null>(null);

  useEffect(() => {
    const ndc = data?.findUniqueProduct?.ndc || null;
    const formatedNdc = ndc?.replace(/-/g, "");
    if (!formatedNdc) return;
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formatedNdc}`;
    axios.get(imageUrlApi).then((response) => {
      setImageUrl(response.data.imageUrl);
    });
  }, [data]);

  const product = data?.findUniqueProduct;
  const headerName = product?.drugName || params?.row?.drugName || "Product details";
  const headerMeta = [product?.ndc || params?.row?.ndc, product?.strength || params?.row?.strength]
    .filter(Boolean)
    .join(" · ");
  const sellerNotes = product?.ProductPackages ?? [];
  const canBuy = params.row.userId !== +id && (product?.quantity ?? 0) > 0;

  const handleClose = () => {
    setParams(null);
    setOpen(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      aria-labelledby="product-dialog-title"
      PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: `1px solid ${LINE_SOFT}`,
          fontFamily: BODY_FONT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
            }}
          >
            <Box
              id="product-dialog-title"
              sx={{
                fontFamily: '"Lexend", sans-serif',
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: "-0.01em",
                color: INK,
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {headerName}
            </Box>
            {product?.isFrozen && (
              <Tooltip title="Refrigerated" arrow placement="top">
                <AcUnitIcon sx={{ fontSize: 20, color: "#4A90D9", flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
          {headerMeta && (
            <Box sx={{ fontFamily: MONO_FONT, fontSize: "12px", color: INK_SOFT }}>{headerMeta}</Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {product && (
            <>
              <Tag
                label={product.packageCondition === 1 ? "Sealed" : "Non-Sealed"}
                tone={product.packageCondition === 1 ? "green" : "amber"}
              />
              <Tag label={product.type === 1 ? "Full" : "Partial"} tone="neutral" />
            </>
          )}
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{ color: INK_SOFT, "&:hover": { bgcolor: LINE_SOFT } }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{ p: 3, bgcolor: "#F8FAFC" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: PRIMARY }} />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <InfoBox title="Drug image">
                <Box
                  sx={{
                    minHeight: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt="Product"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 260,
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: "center", color: INK_SOFT }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "12px",
                          bgcolor: "#F1F5F9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 1.5,
                        }}
                      >
                        <MedicationIcon sx={{ color: PRIMARY, fontSize: 30 }} />
                      </Box>
                      <Box sx={{ fontSize: 13 }}>No image available for this NDC.</Box>
                    </Box>
                  )}
                </Box>
              </InfoBox>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoBox title="Product">
                <DetailRow label="Generic name" value={product.drugName} />
                <DetailRow label="Strength" value={product.strength} />
                <DetailRow label="Form" value={product.form} />
                <DetailRow label="Storage" value={product.isFrozen ? "Refrigerated" : product.storage} />
                <DetailRow label="Labeler/Mfg" value={product.labelerName} />
                <DetailRow label="NDC" value={product.ndc} mono />
              </InfoBox>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoBox title="Package">
                <DetailRow label="Lot number" value={product.lotNumber} mono />
                <DetailRow
                  label="Condition"
                  value={
                    product.packageCondition === 1
                      ? "Sealed"
                      : "Non-Sealed"
                  }
                />
                {product.sealDetails && (
                  <DetailRow label="Condition details" value={getSealDetailsDisplay(product.sealDetails)} />
                )}
                <DetailRow label="Package type" value={product.type === 1 ? "Full" : "Partial"} />
                {product.type === 2 && (
                  <DetailRow label="Partial quantity" value={product.packaging} mono />
                )}
                <DetailRow label="Package quantity" value={product.quantityPerPack} mono />
                <DetailRow label="Expires" value={moment(product.expDate).format("MM/YYYY")} mono />
              </InfoBox>
            </Grid>
            {(product.notes || sellerNotes.length > 0) && (
              <Grid item xs={12}>
                <InfoBox title="Packaging & seller notes">
                  {sellerNotes.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: product.notes ? 1.5 : 0 }}>
                      {sellerNotes.map((item: any) => (
                        <Tag key={item.Package.key} label={item.Package.name} tone="neutral" />
                      ))}
                    </Box>
                  )}
                  {product.notes && (
                    <Box sx={{ fontSize: "13px", color: INK, lineHeight: 1.55 }}>{product.notes}</Box>
                  )}
                </InfoBox>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${LINE_SOFT}`,
          bgcolor: "#F1F5F9",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          fontFamily: BODY_FONT,
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: INK_SOFT,
            }}
          >
            Package price
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
            <Box sx={{ fontFamily: '"Lexend", sans-serif', fontWeight: 700, fontSize: 24, color: INK }}>
              {`$${formatPrice(product?.price || 0)}`}
            </Box>
            <Box sx={{ fontFamily: MONO_FONT, fontSize: "12px", color: INK_SOFT }}>
              {`$${formatPrice(product?.unitPrice || 0)} / unit`}
            </Box>
          </Box>
        </Box>
        {params.row.userId === +id ? (
          <Tag label="My listing" tone="neutral" />
        ) : (
          canBuy && (
            <Box sx={{ minWidth: 300 }}>
              <BuyAction
                key={params.id}
                rowParams={{
                  ...params,
                  row: product
                    ? { ...params.row, available: product.quantity }
                    : params.row,
                }}
                updateCartLoading={cartLoading}
                useAddToCartButton
                handleAvailableCart={async (qty) => {
                  if ((product?.quantity ?? 0) <= 0) {
                    throw new Error("Unavailable");
                  }
                  await handleCart(params, qty);
                }}
              />
            </Box>
          )
        )}
      </Box>
    </Dialog>
  );
};
